import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Bot, Edit, Trash2, Copy, Plus, Search, Share2, Rocket, Compass } from 'lucide-react'
import { useQuery, useQueryClient } from 'react-query'
import { agentService, permissionService } from '../services/api'
import { DefinitionTransfer } from '../components/DefinitionTransfer'
import { AgentPermissionsModal } from '../components/AgentPermissionsModal'
import { useAuthStore } from '../stores/authStore'

export function Agents() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuthStore()
  const [searchTerm, setSearchTerm] = useState('')
  const [permissionsModalAgentId, setPermissionsModalAgentId] = useState<string | null>(null)
  const [discoverableSearchTerm, setDiscoverableSearchTerm] = useState('')
  const [showDiscoverable, setShowDiscoverable] = useState(false)
  
  const { data: agents, isLoading, error } = useQuery(
    'agents',
    agentService.getAllAgents
  )

  // * Fetch discoverable agents (public/shared)
  const { data: discoverableAgents, isLoading: discoverableLoading, refetch: refetchDiscoverable } = useQuery(
    ['discoverable-agents', discoverableSearchTerm],
    () => permissionService.getDiscoverableAgents(discoverableSearchTerm || undefined),
    {
      enabled: showDiscoverable
    }
  )

  // * A model carries no team. Search stays on the name and the description.
  const filteredAgents = agents?.filter(agent => {
    return agent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      agent.description?.toLowerCase().includes(searchTerm.toLowerCase())
  }) || []

  const handleDelete = async (agentId: string, agentName: string) => {
    if (confirm(`Êtes-vous sûr de vouloir supprimer l'agent "${agentName}" ?\n\nCette action est irréversible.`)) {
      try {
        await agentService.deleteAgent(agentId)
        queryClient.invalidateQueries('agents')
      } catch (error) {
        console.error('Erreur lors de la suppression:', error)
        alert('Erreur lors de la suppression de l\'agent')
      }
    }
  }

  const handleDuplicate = async (agentId: string) => {
    try {
      const duplicatedAgent = await agentService.duplicateAgent(agentId, false)
      queryClient.invalidateQueries('agents')
      // * Navigate to edit the duplicated agent
      navigate(`/agents/${duplicatedAgent.id}/edit`)
    } catch (error) {
      console.error('Erreur lors de la duplication:', error)
      alert('Erreur lors de la duplication de l\'agent')
    }
  }

  // * Handle adding discoverable agent as collaborator
  const handleAddDiscoverable = async (agentId: string) => {
    try {
      if (!user?.id) {
        alert('Vous devez être connecté pour ajouter un agent')
        return
      }
      await permissionService.setPermission(agentId, user.id, 'reader')
      refetchDiscoverable()
      queryClient.invalidateQueries('agents')
    } catch (error) {
      console.error('Erreur lors de l\'ajout:', error)
      alert('Erreur lors de l\'ajout de l\'agent')
    }
  }

  // * Handle forking discoverable agent
  const handleForkDiscoverable = async (agentId: string) => {
    try {
      await agentService.duplicateAgent(agentId, true)
      queryClient.invalidateQueries('agents')
      navigate('/agents')
    } catch (error) {
      console.error('Erreur lors du fork:', error)
      alert('Erreur lors du fork de l\'agent')
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 spinner"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Erreur lors du chargement des agents</p>
      </div>
    )
  }

  return (
    <div className="page-content relative min-h-screen">
      {/* Image de robot en arrière-plan */}
      <div className="background-image">
        <img src="/logo.jpg" alt="Robot background" />
      </div>
      
      {/* Contenu au premier plan */}
      <div className="content-wrapper">
    <div className="space-y-6">
      {/* En-tête de page */}
      <div className="page-toolbar flex items-center justify-between">
        <h1 className="text-3xl font-extrabold mb-2 tracking-tight tab_title">Modèles d'agents</h1>
        <div className="flex flex-wrap items-center gap-2">
          <DefinitionTransfer scope="agents" />
          <Link
            to="/agents/new"
            className="button button--primary"
          >
            <Plus className="w-5 h-5" />
            <span>Nouveau modèle d'agent</span>
          </Link>
        </div>
      </div>

      {/* Filtres */}
      <div className="card">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 icon-tertiary" />
          <input id="agents-search" name="agents-search"
            type="text"
            placeholder="Rechercher un agent..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="form-input pl-10"
          />
        </div>
      </div>

      {/* Table des agents */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="table-header">
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider table-header-cell">
                  Nom
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider table-header-cell">
                  Date de création
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider table-header-cell">
                  Créateur
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider table-header-cell">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredAgents.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-12 text-center">
                    <Bot className="w-12 h-12 mx-auto mb-4 icon-light" />
                    <h3 className="text-lg font-medium mb-2 text-primary">Aucun agent trouvé</h3>
                    <p className="mb-4 text-secondary">
                      {searchTerm
                        ? 'Aucun agent ne correspond à la recherche.'
                        : 'Commencez par créer votre premier modèle d\'agent.'}
                    </p>
                    {!searchTerm && (
                      <Link
                        to="/agents/new"
                        className="button button--primary"
                      >
                        <Plus className="w-5 h-5" />
                        <span>Créer un modèle d'agent</span>
                      </Link>
                    )}
                  </td>
                </tr>
              ) : (
                filteredAgents.map((agent, index) => (
                  <tr 
                    key={agent.id}
                    className={`table-row table-hover-row ${index > 0 ? '' : ''}`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Bot className="w-5 h-5 mr-3 text-robot-blue" />
                        <div>
                          <div className="text-sm font-medium text-primary">
                            {agent.name}
                          </div>
                          {agent.description && (
                            <div className="text-sm text-secondary">
                              {agent.description.length > 50 
                                ? `${agent.description.substring(0, 50)}...` 
                                : agent.description}
                            </div>
                          )}
                          <div className="text-xs text-tertiary">
                            {agent.engineName
                              ? `Moteur ${agent.engineName}${agent.engineVersion ? ` ${agent.engineVersion}` : ''}`
                              : 'Aucun moteur'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-primary">
                        {formatDate(agent.createdAt)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-primary">
                        {agent.creatorUsername || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <Link
                          to={`/agents/deploy?agentId=${agent.id}`}
                          className="action-button action-button--primary"
                          title="Déployer"
                        >
                          <Rocket className="w-4 h-4" />
                        </Link>
                        <button
                          onClick={() => setPermissionsModalAgentId(agent.id)}
                          className="action-button action-button--primary"
                          title="Partager / Gérer les droits"
                        >
                          <Share2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => navigate(`/agents/${agent.id}/edit`)}
                          className="action-button action-button--primary"
                          title="Éditer"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDuplicate(agent.id)}
                          className="action-button action-button--primary"
                          title="Dupliquer"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(agent.id, agent.name)}
                          className="action-button action-button--danger"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Section Agents découvrables */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-primary flex items-center">
            <Compass className="w-5 h-5 mr-2" />
            Explorer les modèles publics
          </h2>
          <button
            onClick={() => setShowDiscoverable(!showDiscoverable)}
            className="button button--secondary"
          >
            {showDiscoverable ? 'Masquer' : 'Afficher'}
          </button>
        </div>

        {showDiscoverable && (
          <div className="space-y-4">
            {/* Barre de recherche pour les agents découvrables */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 icon-tertiary" />
              <input id="agents-public-search" name="agents-public-search"
                type="text"
                placeholder="Rechercher un agent public..."
                value={discoverableSearchTerm}
                onChange={(e) => setDiscoverableSearchTerm(e.target.value)}
                className="form-input pl-10"
              />
            </div>

            {/* Table des agents découvrables */}
            {discoverableLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 spinner"></div>
              </div>
            ) : !discoverableAgents || discoverableAgents.length === 0 ? (
              <div className="text-center py-12">
                <Bot className="w-12 h-12 mx-auto mb-4 icon-light" />
                <h3 className="text-lg font-medium mb-2 text-primary">Aucun agent public trouvé</h3>
                <p className="mb-4 text-secondary">
                  {discoverableSearchTerm 
                    ? 'Aucun agent ne correspond à votre recherche.' 
                    : 'Aucun agent public disponible pour le moment.'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="table-header">
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider table-header-cell">
                        Nom
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider table-header-cell">
                        Date de création
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider table-header-cell">
                        Propriétaire
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider table-header-cell">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {discoverableAgents.map((agent, index) => (
                      <tr 
                        key={agent.id}
                        className={`table-row table-hover-row ${index > 0 ? '' : ''}`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Bot className="w-5 h-5 mr-3 text-robot-blue" />
                            <div>
                              <div className="text-sm font-medium text-primary">
                                {agent.name}
                              </div>
                              {agent.description && (
                                <div className="text-sm text-secondary">
                                  {agent.description.length > 50 
                                    ? `${agent.description.substring(0, 50)}...` 
                                    : agent.description}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-primary">
                            {formatDate(agent.createdAt)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-primary">
                            {agent.ownerUsername || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end space-x-2">
                            {agent.userPermission ? (
                              <span className="text-sm text-secondary">
                                Déjà collaborateur
                              </span>
                            ) : (
                              <button
                                onClick={() => handleAddDiscoverable(agent.id)}
                                className="action-button action-button--success"
                                title="S'ajouter comme collaborateur"
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => handleForkDiscoverable(agent.id)}
                              className="action-button action-button--primary"
                              title="Forker (créer une copie privée)"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal de gestion des permissions */}
      {permissionsModalAgentId && (
        <AgentPermissionsModal
          agentId={permissionsModalAgentId}
          isOpen={!!permissionsModalAgentId}
          onClose={() => setPermissionsModalAgentId(null)}
        />
      )}
    </div>
      </div>
    </div>
  )
}
